interface QueuedTask<T> {
  key: string;
  execute: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (reason?: unknown) => void;
}

/** Limits concurrent network work and coalesces callers for the same key. */
export const createDeduplicatedTaskQueue = <T>(concurrency: number) => {
  let running = 0;
  const queued: QueuedTask<T>[] = [];
  const inFlight = new Map<string, Promise<T>>();

  const drain = () => {
    while (running < concurrency && queued.length) {
      const task = queued.shift();
      if (!task) return;

      running += 1;
      task.execute()
        .then(task.resolve, task.reject)
        .finally(() => {
          running -= 1;
          inFlight.delete(task.key);
          drain();
        });
    }
  };

  return (key: string, execute: () => Promise<T>): Promise<T> => {
    const existing = inFlight.get(key);
    if (existing) return existing;

    const promise = new Promise<T>((resolve, reject) => {
      queued.push({ key, execute, resolve, reject });
    });

    inFlight.set(key, promise);
    drain();

    return promise;
  };
};
